/**
 * ZERBAY cart module (hz/62). One ES module per page, printed by the cart hooks
 * (snippets/zerbay-cart-notes.liquid, snippets/zerbay-cart-empty.liquid).
 *
 * What it does, in one place:
 *  - the cart write lock: a FIFO queue where one cart write runs at a time, a 15 s watchdog per job, and
 *    "RENDER DONE" (the job ends only when every cart view shows the cart the server answered with);
 *  - routing of Horizon's own cart controls (Remove, typed quantity, quantity events) through that lock;
 *  - cart errors, the editable quantity of unavailable variants, the note toggle guard;
 *  - the cart drawer's opener after an add, its desktop overlay and no-persist behaviour;
 *  - the checkout button busy state;
 *  - the TR consent box above checkout (step 10): error line, time field and an early cart-attribute save.
 *
 * Public API: `window.Zerbay.cartLock` (frozen), announced by `zerbay:cart-lock-ready` on `document`.
 * Contract with the product page: audit step-3/cart/contracts.md sections 1 and 2.
 * Internal writers may return `{ ok: false, code: 'UNRECONCILED' }` when they cannot tell what the server holds: the
 * lock then takes its unknown-state path (queue suspended, generic error, reload after 1.5 s, no re-read) and `run()`
 * resolves with that result, code kept. The PDP add writer does not use it.
 * Imports use the theme importmap names only (snippets/scripts.liquid); no core file is edited.
 */
import { CartItemsComponent } from '@theme/component-cart-items';
import { StandardEvents } from '@shopify/events';
import { ThemeEvents } from '@theme/events';
import { lockScroll, unlockScroll } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * What a writer returns to `run()`.
 * @typedef {object} WriterResult
 * @property {boolean} ok
 * @property {string} [code] 'INVALID' | 'SERVICE_UNAVAILABLE' | 'UNRECONCILED' (internal writers: cart state unknown,
 *   suspend + reload, `run()` resolves with this result) | anything else (then `cart` is rendered)
 * @property {string|null} [message]
 * @property {{ items: CartItem[] }} [cart] Raw `/cart.js` JSON (or any object with `items`).
 * @property {CartItem[]} [items] Expected cart lines (used by the lock's own Horizon writers).
 * @property {boolean} [noop] Nothing was written, nothing to wait for.
 * @property {() => (void|Promise<void>)} [finish] Runs after RENDER DONE (lock-owned writers only).
 */

/**
 * @typedef {object} CartItem
 * @property {string} key
 * @property {number} variant_id
 * @property {number} quantity
 */

/**
 * The context handed to a writer.
 * @typedef {object} WriterContext
 * @property {AbortSignal} signal Aborted when the watchdog expires.
 * @property {(url: string, init?: RequestInit) => Promise<Response>} fetch Watchdog-armed fetch; the body is already read.
 * @property {() => Promise<any>} readCart GET /cart.js through `fetch`.
 * @property {() => string[]} sectionIds `data-section-id` of every cart view, in DOM order.
 * @property {() => Promise<void>} renderViews Re-renders every cart view from the server.
 */

/**
 * One queued cart write.
 * @typedef {object} Job
 * @property {string} kind 'add' | 'swap' | 'quantity' | 'remove'
 * @property {(ctx: WriterContext) => Promise<WriterResult>} writer
 * @property {{ open?: boolean, line?: Line }} options
 * @property {(result: object) => void} resolve
 * @property {AbortController} [controller]
 * @property {Promise<object>} [timeout] Resolves early when the watchdog expires or the cart state is unknown.
 * @property {(result: object) => void} [timeoutResolve]
 * @property {number} [timer] Watchdog timer id (15 s without progress).
 * @property {boolean} writerRunning True while a non-Horizon writer runs (its lines events are lock-owned).
 * @property {boolean} nativeCall True during the synchronous part of Horizon's `updateQuantity`.
 * @property {boolean} external An external cart write was seen during the job.
 * @property {number} verifyPending External writes whose verification read has not finished.
 * @property {number} verifySeq Version of the newest verification read (taken when the request starts).
 * @property {number} verifyGen Counts external writes seen; the final barrier repeats while it moves.
 * @property {Set<Promise<any>>} verifyTasks Outstanding external-write verifications.
 * @property {CartItem[]|null} expectedLines The lines RENDER DONE last waited for.
 * @property {CartItem[]|null} fresh Expected lines from the newest verification read (wins over the writer's).
 * @property {(() => void)|null} wake Re-runs the RENDER DONE check.
 * @property {boolean} timedOut
 * @property {boolean} failed The cart state could not be established.
 * @property {boolean} released
 * @property {object|null} [unreconciled] The writer's own result after an `UNRECONCILED` code (what `run()` resolves with).
 * @property {Set<() => void>} cleanups
 */

/**
 * A cart line the lock changes through Horizon's own `updateQuantity` (kinds 'quantity' and 'remove').
 * @typedef {object} Line
 * @property {string} key `tr[data-key]` of the row.
 * @property {string} sectionId `data-section-id` of the view that owns the row.
 * @property {number} target Wanted quantity (0 = remove). Updated while the job still waits.
 * @property {number} before The row's server quantity when the job took its turn.
 * @property {number} stock Stock of the variant when tracked + deny (read when the job took its turn), else NaN.
 * @property {HTMLElement|null} view
 * @property {HTMLTableRowElement|null} row
 * @property {number} index Zero-based position among the view's rows when the job ran.
 */

const WATCHDOG_MS = 15000;
const RELOAD_DELAY_MS = 1500;
const RERENDER_AFTER_MS = 3000;
const NOTICE_MS = 8000;
const COMMIT_DELAY_MS = 300;
const SWAP_GRACE_MS = 700;
const CHECKOUT_BUSY_MS = 15000;
const WIDE_QUERY = '(min-width: 990px)';
const CHECKOUT_SELECTOR = '.cart__checkout-button[name=checkout]';
const QTY_INPUT_SELECTOR = 'input[name="updates[]"]';
const KINDS = new Set(['add', 'swap', 'quantity', 'remove']);
const NO_BODY_STATUS = new Set([101, 204, 205, 304]);

/** @type {Job[]} Jobs waiting for their turn. */
const queue = [];
/** @type {Job|null} The job that holds the lock. */
let active = null;
/** True after a watchdog expiry: no further job starts (the page reloads). */
let suspended = false;
/** Timestamp (ms) of the last finished size swap. */
let swapEnd = 0;
/** @type {Map<string, { view: HTMLElement, key: string, raw: string, timer: number }>} Pending 300 ms commits. */
const pendingCommits = new Map();
/** @type {WeakSet<HTMLElement>} Views whose editable-quantity observer is installed. */
const watchedViews = new WeakSet();
/** @type {{ error: string, stock: string }} Last strings read from the strings element. */
let lastStrings = { error: '', stock: '' };
/** @type {HTMLElement|null} */
let noticeEl = null;
/** @type {number} */
let noticeTimer = 0;
/** @type {number} Shared checkout busy timer. */
let checkoutTimer = 0;

/* ------------------------------------------------------------------------------------------------
   Small helpers
   ------------------------------------------------------------------------------------------------ */

/**
 * A locale-aware route. Horizon declares `const Theme` (snippets/scripts.liquid), which is not a window property.
 * @param {string} name Key of `Theme.routes`.
 * @param {string} fallback
 * @returns {string}
 */
function route(name, fallback) {
  return typeof Theme !== 'undefined' ? (Theme.routes?.[name] ?? fallback) : fallback;
}

/** @returns {string} The cart URL with the locale root (Theme.routes.cart_url). */
function cartUrl() {
  return route('cart_url', '/cart');
}

/** @returns {HTMLElement[]} Every cart view in the document, in DOM order. */
function views() {
  return /** @type {HTMLElement[]} */ (Array.from(document.querySelectorAll('cart-items-component')));
}

/** @returns {string[]} Unique section ids of the cart views. */
function sectionIds() {
  return [...new Set(views().map((view) => view.dataset.sectionId).filter(Boolean))];
}

/**
 * @param {HTMLElement} view
 * @returns {'hydration'|'full'} The section render mode Horizon uses for this view.
 */
function renderMode(view) {
  return view.dataset.drawer !== undefined ? 'hydration' : 'full';
}

/** @returns {HTMLElement|null} The cart drawer element (only on pages that render it). */
function getDrawer() {
  return /** @type {HTMLElement|null} */ (document.querySelector('theme-drawer#cart-drawer'));
}

/**
 * Rows of a view that carry a cart line (template content is not entered by querySelectorAll).
 * @param {ParentNode} view
 * @returns {HTMLTableRowElement[]}
 */
function lineRows(view) {
  return /** @type {HTMLTableRowElement[]} */ (Array.from(view.querySelectorAll('tr[data-key]')));
}

/**
 * @param {ParentNode} view
 * @param {string} key
 * @returns {HTMLTableRowElement|null}
 */
function findRow(view, key) {
  return lineRows(view).find((row) => row.dataset.key === key) ?? null;
}

/**
 * The quantity the server rendered for a row: the `data-zb-qty` marker (zerbay-cart-line-quantity), else the input's
 * `value` attribute. Never the user-typed `.value`.
 * @param {Element} row
 * @returns {number}
 */
function serverQuantity(row) {
  const marker = row.querySelector('[data-zb-qty]');
  if (marker) return Number(marker.getAttribute('data-zb-qty'));
  return Number(row.querySelector(QTY_INPUT_SELECTOR)?.getAttribute('value'));
}

/**
 * Adds a class only when missing, so a MutationObserver is not woken by a no-op write.
 * @param {Element} element
 * @param {string} name
 */
function addClass(element, name) {
  if (!element.classList.contains(name)) element.classList.add(name);
}

/**
 * Runs `fn` on the next animation frame, or after 100 ms when frames are paused (background tab).
 * @param {() => void} fn
 */
function afterFrame(fn) {
  let done = false;
  const run = () => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    cancelAnimationFrame(frame);
    fn();
  };
  const frame = requestAnimationFrame(run);
  const timer = setTimeout(run, 100);
}

/**
 * Strings printed by the cart snippets, read at use time (a morph re-renders the element).
 * @returns {{ error: string, stock: string }}
 */
function strings() {
  const element = /** @type {HTMLElement|null} */ (document.querySelector('[data-zb-cart-strings]'));
  if (element) {
    lastStrings = {
      error: element.dataset.error ?? lastStrings.error,
      stock: element.dataset.stock ?? lastStrings.stock,
    };
  }
  return lastStrings;
}

/**
 * @param {any[]|undefined|null} items
 * @returns {CartItem[]} Items reduced to the three fields RENDER DONE compares.
 */
function normalizeItems(items) {
  return (items ?? []).map((item) => ({
    key: String(item.key),
    variant_id: Number(item.variant_id),
    quantity: Number(item.quantity),
  }));
}

/* ------------------------------------------------------------------------------------------------
   Busy state (html attribute, Horizon's own disabled class, checkout buttons)
   ------------------------------------------------------------------------------------------------ */

/**
 * Applies (idempotently) every visible sign of a held lock; called again after anything that may morph the views.
 * @param {string} kind
 */
function applyBusy(kind) {
  const root = document.documentElement;
  if (root.getAttribute('data-zb-cart-busy') !== kind) root.setAttribute('data-zb-cart-busy', kind);
  for (const view of views()) addClass(view, 'cart-items-disabled');
  for (const button of document.querySelectorAll(CHECKOUT_SELECTOR)) {
    if (button.getAttribute('aria-disabled') !== 'true') {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('data-zb-lock-disabled', '');
    }
  }
}

/** Removes everything `applyBusy` added; leaves a checkout button that is `aria-busy` (section 8) disabled. */
function clearBusy() {
  document.documentElement.removeAttribute('data-zb-cart-busy');
  for (const view of views()) {
    view.classList.remove('cart-items-disabled');
    for (const row of view.querySelectorAll('.zb-line-busy')) row.classList.remove('zb-line-busy');
  }
  for (const button of document.querySelectorAll('[data-zb-lock-disabled]')) {
    button.removeAttribute('data-zb-lock-disabled');
    if (button.getAttribute('aria-busy') !== 'true') button.removeAttribute('aria-disabled');
  }
}

/* ------------------------------------------------------------------------------------------------
   Watchdog: 15 s without progress, from the job's turn until its release
   ------------------------------------------------------------------------------------------------ */

/**
 * Marks progress: restarts the job's 15 s timer. Called when the job takes its turn, at every request start and
 * end, and when the RENDER DONE wait starts. Does nothing after the job was released (a late `ctx.fetch`).
 * @param {Job} job
 */
function progress(job) {
  if (job.timedOut || job.failed || job.released) return;
  clearTimeout(job.timer);
  job.timer = window.setTimeout(() => expire(job), WATCHDOG_MS);
}

/**
 * @param {Job} job
 * @returns {boolean} True when the job no longer owns the lock, so it must not touch the page.
 */
function isStale(job) {
  return job.timedOut || job.failed || job.released || active !== job;
}

/**
 * @param {Job} job
 * @returns {object} The result `run()` resolves with when the job ended early.
 */
function staleResult(job) {
  if (job.unreconciled) return job.unreconciled;
  return job.failed ? { ok: false, code: 'SERVICE_UNAVAILABLE', message: null } : { ok: false, code: 'TIMEOUT', timeout: true };
}

/**
 * Stops the queue for good: nothing else may write against rows that may be stale. Reloads after 1.5 s.
 */
function stopQueue() {
  suspended = true;
  setTimeout(() => location.reload(), RELOAD_DELAY_MS);
  for (const waiting of queue.splice(0)) waiting.resolve({ ok: false, code: 'TIMEOUT', timeout: true });
}

/**
 * Ends a job early (watchdog or unknown cart state): abort, clean up, show the generic error, resolve `run()`.
 * An add shows no notice of its own: the product page shows the error under its form.
 * @param {Job} job
 * @param {boolean} timeout True for a watchdog expiry, false when the cart state cannot be established.
 */
function endEarly(job, timeout) {
  if (job.timedOut || job.failed || job.released) return;
  if (timeout) job.timedOut = true;
  else job.failed = true;
  clearTimeout(job.timer);
  stopQueue();
  job.timeoutResolve?.(staleResult(job));
  try {
    job.controller?.abort();
    for (const id of sectionIds()) sectionRenderer.abortRender(id);
    runCleanups(job);
    if (job.kind !== 'add') showError(strings().error, job.options.line?.row ?? null);
  } catch (error) {
    console.warn('[zerbay-cart] clean-up after an aborted job failed:', error);
  }
}

/**
 * Watchdog expiry.
 * @param {Job} job
 */
function expire(job) {
  endEarly(job, true);
}

/**
 * The cart state cannot be established (failed read or render, exception while settling): suspend and reload.
 * @param {Job} job
 */
function failUnknown(job) {
  endEarly(job, false);
}

/**
 * @param {Job} job
 */
function runCleanups(job) {
  for (const cleanup of [...job.cleanups]) cleanup();
  job.cleanups.clear();
}

/* ------------------------------------------------------------------------------------------------
   RENDER DONE
   ------------------------------------------------------------------------------------------------ */

/**
 * Does one view show exactly the expected cart lines?
 * @param {HTMLElement} view
 * @param {CartItem[]} expected
 * @returns {boolean}
 */
function viewMatches(view, expected) {
  const rows = lineRows(view);
  if (expected.length === 0) {
    return rows.length === 0 && view.querySelector('.zb-cart-empty__text, [data-cart-drawer-empty]') !== null;
  }
  if (rows.length !== expected.length) return false;
  const byKey = new Map(expected.map((item) => [item.key, item]));
  if (byKey.size !== expected.length) return false;
  return rows.every((row) => {
    const item = byKey.get(row.dataset.key ?? '');
    if (!item) return false;
    const variant = /** @type {HTMLElement|null} */ (row.querySelector('[data-variant-id]'));
    return Number(variant?.dataset.variantId) === item.variant_id && serverQuantity(row) === item.quantity;
  });
}

/**
 * Section render options for a view (the stylesheet is injected when an empty drawer gets filled, like Horizon).
 * @param {HTMLElement} view
 * @returns {{ cache: false, mode: 'hydration'|'full', injectStylesheet: boolean }}
 */
function renderOptions(view) {
  const emptyDrawer = view.dataset.drawer !== undefined && view.querySelector('[data-cart-drawer-empty]') !== null;
  return { cache: false, mode: renderMode(view), injectStylesheet: emptyDrawer };
}

/**
 * Waits until every cart view matches the expected lines (`job.fresh` wins over `state.expected`) and no external
 * write is being verified. Re-renders views that still differ after 3 s; if they still differ, adopts one fresh
 * /cart.js read as the expected state. Resolves also when the job ended early.
 * @param {Job} job
 * @param {{ expected: CartItem[] }} state
 * @returns {Promise<void>}
 */
function waitForViews(job, state) {
  return new Promise((resolve) => {
    /** @type {Map<HTMLElement, MutationObserver>} */
    const observers = new Map();
    let rerenderTimer = 0;
    let finished = false;
    let adopted = false;

    const stop = () => {
      clearTimeout(rerenderTimer);
      job.wake = null;
      for (const observer of observers.values()) observer.disconnect();
      observers.clear();
      job.cleanups.delete(stop);
    };
    const finish = () => {
      if (finished) return;
      finished = true;
      stop();
      resolve();
    };
    const expected = () => job.fresh ?? state.expected;
    const allMatch = () => views().every((view) => viewMatches(view, expected()));
    const schedule = () => afterFrame(check);
    const syncObservers = () => {
      const current = new Set(views());
      for (const [view, observer] of observers) {
        if (current.has(view)) continue;
        observer.disconnect();
        observers.delete(view);
      }
      for (const view of current) {
        if (observers.has(view)) continue;
        const observer = new MutationObserver(schedule);
        observer.observe(view, { childList: true, subtree: true, attributes: true, characterData: true });
        observers.set(view, observer);
      }
    };
    const check = () => {
      if (finished) return;
      if (isStale(job)) return finish();
      applyBusy(job.kind);
      syncObservers();
      if (job.verifyPending > 0) return;
      if (allMatch()) finish();
    };
    const rerender = async () => {
      if (finished || isStale(job)) return;
      await awaitVerifications(job);
      if (finished || isStale(job) || allMatch()) return;
      const bad = views().filter((view) => !viewMatches(view, expected()));
      const results = await Promise.allSettled(
        bad.map((view) => (view.dataset.sectionId ? sectionRenderer.renderSection(view.dataset.sectionId, renderOptions(view)) : null))
      );
      if (finished || isStale(job)) return;
      if (results.some((result) => result.status === 'rejected' && result.reason?.name !== 'AbortError')) {
        failUnknown(job);
        return;
      }
      check();
      if (finished || adopted) return;
      adopted = true;
      try {
        await awaitVerifications(job);
        if (isStale(job) || allMatch()) return;
        await verifyRead(job);
        check();
      } catch {
        failUnknown(job);
      }
    };

    progress(job);
    job.expectedLines = state.expected;
    job.cleanups.add(stop);
    job.wake = schedule;
    rerenderTimer = window.setTimeout(rerender, RERENDER_AFTER_MS);
    check();
  });
}

/**
 * GET /cart.js through the job's watchdog. Strict: a non-OK status or a payload without an `items` array rejects.
 * @param {Job} job
 * @returns {Promise<any>}
 */
async function readCartDirect(job) {
  const response = await jobFetch(job, `${cartUrl()}.js`, {
    headers: { Accept: 'application/json' },
    cache: 'no-store',
  });
  if (!response.ok) throw new Error(`Cart read failed: ${response.status}`);
  const cart = await response.json();
  if (!cart || !Array.isArray(cart.items)) throw new Error('Cart read returned no items');
  return cart;
}

/**
 * One verification read. The version is taken when the request STARTS; the result becomes the expected state only
 * if no newer read started meanwhile.
 * @param {Job} job
 * @returns {Promise<boolean>} Whether the result was adopted.
 */
async function verifyRead(job) {
  const version = ++job.verifySeq;
  const cart = await readCartDirect(job);
  if (isStale(job) || version !== job.verifySeq) return false;
  job.fresh = normalizeItems(cart.items);
  return true;
}

/**
 * Waits for every outstanding external-write verification (including ones that start while waiting).
 * @param {Job} job
 * @returns {Promise<void>}
 */
async function awaitVerifications(job) {
  while (job.verifyTasks.size > 0) await Promise.allSettled([...job.verifyTasks]);
}

/**
 * RENDER DONE: waits until every view shows the expected cart. After an external write the expected state is
 * one fresh /cart.js read, taken after every pending external verification (so it cannot overtake one).
 * @param {Job} job
 * @param {CartItem[]} items
 * @returns {Promise<void>}
 */
async function renderDone(job, items) {
  const state = { expected: normalizeItems(items) };
  if (job.external) {
    await awaitVerifications(job);
    if (isStale(job)) return;
    await verifyRead(job);
  }
  if (isStale(job)) return;
  await waitForViews(job, state);
}

/**
 * Final barrier before release: repeats verification and view matching until no external write arrived during the
 * last pass and the views show the expected lines. The lock is released in the same synchronous step as the last
 * successful check, so nothing can slip in between.
 * @param {Job} job
 * @returns {Promise<boolean>} True when the barrier passed and the job was released; false when the job ended early.
 */
async function finalBarrier(job) {
  let seen = -1;
  while (!isStale(job)) {
    const expected = job.fresh ?? job.expectedLines;
    const matches = !expected || views().every((view) => viewMatches(view, expected));
    if (job.verifyGen === seen && job.verifyPending === 0 && matches) {
      release(job);
      return true;
    }
    seen = job.verifyGen;
    await awaitVerifications(job);
    if (isStale(job)) return false;
    if (job.expectedLines || job.fresh) await waitForViews(job, { expected: job.expectedLines ?? [] });
  }
  return false;
}

/**
 * An external cart write was seen during the job (R2-04). Completion is blocked at once; once the write's own
 * request has settled (its event is dispatched before the request), /cart.js is read (versioned at read start).
 * @param {Job} job
 * @param {Promise<any>|undefined} promise The event's promise, when it has one.
 */
function markExternal(job, promise) {
  job.external = true;
  job.verifyPending += 1;
  job.verifyGen += 1;
  const task = Promise.allSettled([promise])
    .then(() => (isStale(job) ? false : verifyRead(job)))
    .catch(() => failUnknown(job))
    .finally(() => {
      job.verifyPending -= 1;
      job.verifyTasks.delete(task);
      job.wake?.();
    });
  job.verifyTasks.add(task);
}

/* ------------------------------------------------------------------------------------------------
   Jobs
   ------------------------------------------------------------------------------------------------ */

/**
 * `ctx.fetch`: progress marked at start and end, body read as text, then a fresh Response is returned.
 * @param {Job} job
 * @param {string} url
 * @param {RequestInit} [init]
 * @returns {Promise<Response>}
 */
async function jobFetch(job, url, init = {}) {
  progress(job);
  try {
    const response = await fetch(url, { ...init, signal: job.controller?.signal });
    const text = await response.text();
    return new Response(NO_BODY_STATUS.has(response.status) ? null : text, {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  } finally {
    progress(job);
  }
}

/**
 * @param {Job} job
 * @returns {WriterContext}
 */
function makeContext(job) {
  const ctx = {
    signal: /** @type {AbortSignal} */ (job.controller?.signal),
    fetch: (/** @type {string} */ url, /** @type {RequestInit|undefined} */ init) => jobFetch(job, url, init),
    readCart: () => readCartDirect(job),
    sectionIds,
    renderViews: async () => {
      progress(job);
      try {
        await Promise.all(
          views().map((view) =>
            view.dataset.sectionId ? sectionRenderer.renderSection(view.dataset.sectionId, renderOptions(view)) : null
          )
        );
      } finally {
        progress(job);
      }
    },
  };
  return ctx;
}

/**
 * Starts the next waiting job, if the lock is free.
 */
async function pump() {
  if (active || suspended) return;
  const job = queue.shift();
  if (!job) return;
  active = job;
  /** @type {object} */
  let result;
  try {
    result = await execute(job);
  } catch (error) {
    console.warn('[zerbay-cart] job failed:', error);
    result = { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
  }
  if (job.unreconciled) {
    // Cart state unknown: the lock stays busy (attribute, disabled classes, checkout aria-disabled) and the queue
    // stays suspended until the page reloads.
    job.resolve(result);
    return;
  }
  release(job);
  job.resolve(result);
  pump();
}

/**
 * Marks the lock busy, waits for Horizon's own pending writes, runs the writer and settles its result.
 * The watchdog runs from here to the release.
 * @param {Job} job
 * @returns {Promise<object>}
 */
async function execute(job) {
  job.controller = new AbortController();
  job.timeout = new Promise((resolve) => {
    job.timeoutResolve = resolve;
  });
  progress(job);
  watchViews();
  applyBusy(job.kind);
  document.dispatchEvent(new CustomEvent('zerbay:cart-lock', { detail: { busy: true, kind: job.kind } }));
  const work = (async () => {
    await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
    return isStale(job) ? staleResult(job) : runJob(job);
  })();
  work.catch(() => {});
  return Promise.race([work, job.timeout]);
}

/**
 * Calls the writer and settles what it returns (contract section 1.3).
 * @param {Job} job
 * @returns {Promise<object>}
 */
async function runJob(job) {
  const ctx = makeContext(job);
  /** @type {WriterResult} */
  let result;
  job.writerRunning = !job.options.line;
  try {
    result = await job.writer(ctx);
  } catch (error) {
    if (!isStale(job)) console.warn('[zerbay-cart] writer threw:', error);
    result = { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
  } finally {
    job.writerRunning = false;
  }
  if (isStale(job)) return staleResult(job);
  applyBusy(job.kind);
  try {
    const settled = await settle(job, ctx, result);
    return (await finalBarrier(job)) ? settled : staleResult(job);
  } catch (error) {
    if (!isStale(job)) console.warn('[zerbay-cart] settling the write failed:', error);
    failUnknown(job);
    return { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
  }
}

/**
 * Turns a writer result into the lock's actions and the value `run()` resolves with.
 * @param {Job} job
 * @param {WriterContext} ctx
 * @param {WriterResult} result
 * @returns {Promise<object>}
 */
async function settle(job, ctx, result) {
  if (!result || typeof result !== 'object') result = { ok: false, code: 'SERVICE_UNAVAILABLE', message: null };
  const { finish, items, noop, ...publicResult } = result;

  if (result.ok !== true && result.code === 'UNRECONCILED') {
    job.unreconciled = publicResult;
    failUnknown(job);
    return staleResult(job);
  }

  if (result.ok === true) {
    if (!noop) {
      const expected = items ?? result.cart?.items ?? (await ctx.readCart()).items;
      if (isStale(job)) return staleResult(job);
      await renderDone(job, expected);
    }
    if (isStale(job)) return staleResult(job);
    await runFinish(job, finish);
    if (isStale(job)) return staleResult(job);
    if (job.kind !== 'add') return { ok: true };
    const opened = job.options.open === false ? false : await openDrawer();
    return isStale(job) ? staleResult(job) : { ok: true, opened };
  }

  if (result.code === 'SERVICE_UNAVAILABLE') {
    await recoverFromUnknown(job, ctx);
  } else if (result.code !== 'INVALID' && result.cart) {
    await renderDone(job, result.cart.items);
  } else if (job.external) {
    await renderDone(job, []);
  }
  if (isStale(job)) return staleResult(job);
  await runFinish(job, finish);
  return publicResult;
}

/**
 * The write may or may not have reached the server: read the cart once and re-render when a view differs.
 * A failed read means the state is unknown: suspend and reload.
 * @param {Job} job
 * @param {WriterContext} ctx
 */
async function recoverFromUnknown(job, ctx) {
  let cart;
  try {
    cart = await ctx.readCart();
  } catch (error) {
    if (!isStale(job)) console.warn('[zerbay-cart] cart read after a failed write failed:', error);
    failUnknown(job);
    return;
  }
  if (isStale(job)) return;
  const expected = normalizeItems(cart.items);
  if (!job.external && views().every((view) => viewMatches(view, expected))) return;
  await ctx.renderViews();
  if (isStale(job)) return;
  await renderDone(job, cart.items);
}

/**
 * @param {Job} job
 * @param {(() => (void|Promise<void>))|undefined} finish
 */
async function runFinish(job, finish) {
  if (!finish || isStale(job)) return;
  try {
    await finish();
  } catch (error) {
    console.warn('[zerbay-cart] finish step failed:', error);
  }
}

/**
 * Release (spec section 2, step 5). Idempotent: a second call for the same job does nothing.
 * @param {Job} job
 */
function release(job) {
  if (job.released) return;
  job.released = true;
  active = null;
  runCleanups(job);
  clearTimeout(job.timer);
  job.wake = null;
  clearBusy();
  document.dispatchEvent(new CustomEvent('zerbay:cart-lock', { detail: { busy: false, kind: job.kind } }));
}

/**
 * Creates a job and queues it.
 * @param {'add'|'swap'|'quantity'|'remove'} kind
 * @param {(ctx: WriterContext) => Promise<WriterResult>} writer
 * @param {{ open?: boolean, line?: Line }} options
 * @returns {{ job: Job|null, promise: Promise<object> }}
 */
function enqueue(kind, writer, options) {
  if (suspended) return { job: null, promise: Promise.resolve({ ok: false, code: 'TIMEOUT', timeout: true }) };
  /** @type {Job|null} */
  let created = null;
  const promise = new Promise((resolve) => {
    created = {
      kind,
      writer,
      options,
      resolve,
      writerRunning: false,
      nativeCall: false,
      external: false,
      verifyPending: 0,
      verifySeq: 0,
      verifyGen: 0,
      verifyTasks: new Set(),
      expectedLines: null,
      fresh: null,
      wake: null,
      timedOut: false,
      failed: false,
      released: false,
      cleanups: new Set(),
    };
    queue.push(created);
  });
  queueMicrotask(pump);
  return { job: created, promise };
}

/**
 * Queues a cart write. Never rejects.
 * @param {'add'|'swap'|'quantity'|'remove'} kind
 * @param {(ctx: WriterContext) => Promise<WriterResult>} writer
 * @param {{ open?: boolean }} [options]
 * @returns {Promise<object>} `{ ok: true, opened }` for an add, `{ ok: true }` otherwise, or the writer's failure result.
 */
function run(kind, writer, options = {}) {
  if (!KINDS.has(kind) || typeof writer !== 'function') {
    console.warn('[zerbay-cart] run() needs a kind (add|swap|quantity|remove) and a writer function');
    return Promise.resolve({ ok: false, code: 'INVALID', message: null });
  }
  return enqueue(kind, writer, { open: options?.open }).promise;
}

/**
 * External-write detection (R2-04): a lines event during a held lock that the lock did not cause.
 * @param {Event & { promise?: Promise<any> }} event
 */
function onLinesUpdate(event) {
  const job = active;
  if (!job || isStale(job) || job.writerRunning || job.nativeCall) return;
  markExternal(job, event.promise);
}

/**
 * A discount update (always) or a note update from outside the cart views, during a held lock: it can change
 * lines or prices without a lines event, so it counts as an external write.
 * @param {Event & { promise?: Promise<any> }} event
 */
function onOtherCartWrite(event) {
  const job = active;
  if (!job || isStale(job)) return;
  const inside = event.target instanceof Element && event.target.closest('cart-items-component') !== null;
  if (event.type === StandardEvents.cartNoteUpdate && inside) return;
  markExternal(job, event.promise);
}

/* ------------------------------------------------------------------------------------------------
   Errors
   ------------------------------------------------------------------------------------------------ */

/**
 * The error cell and its container of a row, found at the row's CURRENT line number in its view.
 * @param {HTMLElement} view
 * @param {Element} row
 * @returns {{ text: HTMLElement, box: HTMLElement }|null}
 */
function errorCell(view, row) {
  const number = lineRows(view).indexOf(/** @type {HTMLTableRowElement} */ (row)) + 1;
  if (number < 1) return null;
  const text = /** @type {HTMLElement|null} */ (view.querySelector(`[ref="cartItemError-${number}"]`));
  const box = /** @type {HTMLElement|null} */ (view.querySelector(`[ref="cartItemErrorContainer-${number}"]`));
  return text && box ? { text, box } : null;
}

/**
 * Shows an error in the row's line cell, or in the bottom notice when there is no row.
 * @param {string} text
 * @param {Element|null} [row] The cart row; ignored when it is no longer in the document.
 */
function showError(text, row = null) {
  if (!text) return;
  const view = row?.isConnected ? row.closest('cart-items-component') : null;
  const cell = view ? errorCell(/** @type {HTMLElement} */ (view), /** @type {Element} */ (row)) : null;
  if (cell) {
    cell.text.textContent = text;
    cell.box.classList.remove('hidden');
    return;
  }
  showNotice(text);
}

/**
 * The fixed bottom notice (one at a time, removed after 8 s). It is placed inside the open modal dialog (so it is
 * not inert and is announced) AND shown as a manual popover, which lifts it into the top layer above the drawer;
 * without popover support it stays inside the modal, else in body.
 * @param {string} text
 */
function showNotice(text) {
  if (!text) return;
  hideNotice();
  const notice = document.createElement('div');
  notice.className = 'zb-cart-notice';
  notice.setAttribute('role', 'alert');
  notice.textContent = text;
  const popover = typeof notice.showPopover === 'function';
  if (popover) notice.setAttribute('popover', 'manual');
  (noticeHost() ?? document.body).append(notice);
  if (popover) {
    try {
      notice.showPopover();
    } catch {
      notice.removeAttribute('popover');
    }
  }
  noticeEl = notice;
  noticeTimer = window.setTimeout(hideNotice, NOTICE_MS);
}

/**
 * The open modal dialog that should hold the notice: the cart drawer's, else the last modal dialog in the document.
 * `:modal` throws on older engines; then any open dialog is used.
 * @returns {Element|null}
 */
function noticeHost() {
  try {
    return document.querySelector('#cart-drawer dialog:modal') ?? Array.from(document.querySelectorAll('dialog:modal')).pop() ?? null;
  } catch {
    return document.querySelector('#cart-drawer dialog[open]') ?? document.querySelector('dialog[open]');
  }
}

function hideNotice() {
  clearTimeout(noticeTimer);
  noticeEl?.remove();
  noticeEl = null;
}

/**
 * A cart error nobody owns (no job active): show the generic text as a notice.
 * @param {Event & { code?: string, detail?: { code?: string } }} event
 */
function onCartError(event) {
  if (active || suspended) return;
  const code = event.code ?? event.detail?.code;
  if (code === 'SERVICE_UNAVAILABLE') showNotice(strings().error);
}

/* ------------------------------------------------------------------------------------------------
   Horizon writes inside the lock (quantity / remove)
   ------------------------------------------------------------------------------------------------ */

/**
 * @param {{ view: HTMLElement|null, sectionId: string }} line
 * @returns {HTMLElement|null} The stored view while connected, else the view with the same section id.
 */
function resolveView(line) {
  if (line.view?.isConnected) return line.view;
  return views().find((view) => view.dataset.sectionId === line.sectionId) ?? null;
}

/**
 * @param {Element} row
 */
function restoreInput(row) {
  const input = /** @type {HTMLInputElement|null} */ (row.querySelector(QTY_INPUT_SELECTOR));
  if (input) input.value = input.getAttribute('value') ?? '';
}

/**
 * Routes a wanted quantity for a row (0 = remove) into the queue, keeping the order of the shopper's changes:
 * - the LAST waiting job of that row is a quantity job: it takes the new target (and is dropped when the target
 *   equals the server value and nothing for the row is running); a remove target turns it into a remove;
 * - the last is a waiting remove: a remove again is a duplicate; a later non-zero quantity retargets it (it becomes
 *   a quantity job);
 * - a job for the row is running: the same target is a duplicate, a different one (also a reversal back to the
 *   server value) queues a new job;
 * - nothing for the row: equal to the server value is a no-op.
 * @param {HTMLElement} view
 * @param {Element} row
 * @param {number} target
 */
function routeQuantity(view, row, target) {
  if (suspended) return;
  const key = /** @type {HTMLElement} */ (row).dataset.key ?? '';
  const sectionId = view.dataset.sectionId ?? '';
  const same = (/** @type {Job} */ job) => job.options.line?.key === key && job.options.line.sectionId === sectionId;
  const kind = target === 0 ? 'remove' : 'quantity';
  const server = serverQuantity(row);

  let waitingIndex = -1;
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    if (same(queue[index])) {
      waitingIndex = index;
      break;
    }
  }
  const waiting = waitingIndex >= 0 ? queue[waitingIndex] : null;
  const running = active && same(active) ? active : null;

  if (waiting?.kind === 'quantity' && waiting.options.line) {
    if (target === server && !running) {
      queue.splice(waitingIndex, 1);
      waiting.resolve({ ok: true });
      restoreInput(row);
      return;
    }
    waiting.options.line.target = target;
    waiting.kind = kind;
    return;
  }
  if (waiting?.kind === 'remove' && waiting.options.line) {
    if (target !== 0) {
      waiting.options.line.target = target;
      waiting.kind = 'quantity';
    }
    return;
  }
  if (running) {
    if (running.options.line?.target === target) return;
  } else if (target === server) {
    restoreInput(row);
    return;
  }
  enqueueLine(kind, view, row, target);
}

/**
 * Queues a quantity or remove job for a row.
 * @param {'quantity'|'remove'} kind
 * @param {HTMLElement} view
 * @param {Element} row
 * @param {number} target Wanted quantity (0 for remove).
 */
function enqueueLine(kind, view, row, target) {
  /** @type {Line} */
  const line = {
    key: /** @type {HTMLElement} */ (row).dataset.key ?? '',
    sectionId: view.dataset.sectionId ?? '',
    target,
    before: serverQuantity(row),
    stock: NaN,
    view,
    row: /** @type {any} */ (row),
    index: -1,
  };
  /** @type {Job|null} */
  let job = null;
  ({ job } = enqueue(kind, (ctx) => writeLine(line, ctx, /** @type {Job} */ (job)), { line }));
}

/**
 * The writer of a quantity / remove job: Horizon's own `updateQuantity`, called inside the lock.
 * @param {Line} line
 * @param {WriterContext} _ctx
 * @param {Job} job
 * @returns {Promise<WriterResult>}
 */
async function writeLine(line, _ctx, job) {
  const view = resolveView(line);
  const row = view ? findRow(view, line.key) : null;
  if (!view || !row) return { ok: true, noop: true };
  if (typeof (/** @type {any} */ (view).updateQuantity) !== 'function') {
    return { ok: false, code: 'SERVICE_UNAVAILABLE', message: null, finish: () => finishLine(line, 'SERVICE_UNAVAILABLE') };
  }

  const rows = lineRows(view);
  line.view = view;
  line.row = row;
  line.index = rows.indexOf(row);
  line.before = serverQuantity(row);
  const stockAttribute = row.querySelector('[data-zb-stock]')?.getAttribute('data-zb-stock');
  line.stock = stockAttribute === null || stockAttribute === undefined ? NaN : Number(stockAttribute);
  const quantity = line.target;
  if (quantity === line.before) {
    restoreInput(row);
    return { ok: true, noop: true };
  }

  row.querySelector(QTY_INPUT_SELECTOR)?.removeAttribute('data-skip-value-update');
  const cell = errorCell(view, row);
  cell?.box.classList.add('hidden');
  addClass(row, 'zb-line-busy');

  /** @type {{ promise?: Promise<any> }|null} */
  let linesEvent = null;
  let errorCode = '';
  const onLines = (/** @type {any} */ event) => {
    linesEvent = event;
  };
  const onError = (/** @type {any} */ event) => {
    if (event.target === view) errorCode = errorCode || event.code || event.detail?.code || '';
  };
  const detach = () => {
    view.removeEventListener(StandardEvents.cartLinesUpdate, onLines);
    view.removeEventListener(StandardEvents.cartError, onError);
    job.cleanups.delete(detach);
  };
  view.addEventListener(StandardEvents.cartLinesUpdate, onLines, { once: true });
  view.addEventListener(StandardEvents.cartError, onError);
  job.cleanups.add(detach);

  progress(job);
  let call;
  job.nativeCall = true;
  try {
    call = /** @type {any} */ (view).updateQuantity({ line: line.index + 1, quantity, action: quantity === 0 ? 'clear' : 'change' });
  } finally {
    job.nativeCall = false;
  }
  const eventPromise = linesEvent?.promise ?? Promise.reject(new Error('No lines event'));
  const [, eventResult] = await Promise.allSettled([call, eventPromise]);
  detach();
  if (isStale(job)) return { ok: true, noop: true };
  progress(job);
  applyBusy(job.kind);

  if (eventResult.status === 'fulfilled') {
    const items = eventResult.value?.detail?.items;
    return { ok: true, items: Array.isArray(items) ? items : undefined, finish: () => finishLine(line, 'OK', items) };
  }
  const answered = errorCode ? errorCode === 'INVALID' : cell ? !cell.box.classList.contains('hidden') : false;
  const code = answered ? 'INVALID' : 'SERVICE_UNAVAILABLE';
  return { ok: false, code, message: null, finish: () => finishLine(line, code) };
}

/**
 * After RENDER DONE: stock decision (L-22), error text, field reset, focus after a remove, line busy state.
 * @param {Line} line
 * @param {'OK'|'INVALID'|'SERVICE_UNAVAILABLE'} outcome
 * @param {any[]} [items] The answer's lines (outcome 'OK').
 */
function finishLine(line, outcome, items) {
  const view = resolveView(line);
  if (!view) return;
  for (const busy of view.querySelectorAll('.zb-line-busy')) busy.classList.remove('zb-line-busy');
  const row = findRow(view, line.key);

  if (!row) {
    if (outcome === 'OK' && line.target === 0) focusAfterRemove(view, line.index);
    return;
  }

  const input = /** @type {HTMLInputElement|null} */ (row.querySelector(QTY_INPUT_SELECTOR));
  if (outcome !== 'OK' && input) input.value = input.getAttribute('value') ?? input.value;

  const after = outcome === 'OK' ? items?.find((item) => String(item.key) === line.key)?.quantity : undefined;
  const text = lineErrorText(line, outcome, after ?? serverQuantity(row));
  if (text) showError(text, row);
}

/**
 * L-22: the text for a line after a write, or '' for none.
 * @param {Line} line
 * @param {'OK'|'INVALID'|'SERVICE_UNAVAILABLE'} outcome
 * @param {number} after The line's server quantity after the write.
 * @returns {string}
 */
function lineErrorText(line, outcome, after) {
  const asked = line.target;
  const raise = asked > line.before && Number.isFinite(line.stock) && asked > line.stock;
  if (outcome === 'OK') {
    const capped = after < asked;
    return raise && (after === line.before || capped) ? strings().stock : '';
  }
  return raise && outcome === 'INVALID' ? strings().stock : strings().error;
}

/**
 * Moves focus after a remove (CART-21): the Remove button of the row now at the old position, else the
 * previous row's, else the view's heading.
 * @param {HTMLElement} view
 * @param {number} index Zero-based position of the removed row.
 */
function focusAfterRemove(view, index) {
  const rows = lineRows(view);
  const remove = (/** @type {number} */ position) => {
    const button = /** @type {HTMLElement|null} */ (rows[position]?.querySelector('.cart-items__remove') ?? null);
    return button && button.getClientRects().length > 0 && !button.closest('[hidden]') ? button : null;
  };
  const target = remove(index) ?? remove(index - 1);
  if (target) {
    target.focus();
    return;
  }
  const heading = /** @type {HTMLElement|null} */ (
    view.dataset.drawer !== undefined
      ? document.getElementById('cart-drawer-heading')
      : document.querySelector('.cart-title h1')
  );
  if (!heading) return;
  heading.setAttribute('tabindex', '-1');
  heading.focus();
}

/* ------------------------------------------------------------------------------------------------
   Control routing (window capture listeners; run before Horizon's document capture delegation)
   ------------------------------------------------------------------------------------------------ */

/**
 * @param {EventTarget|null} target
 * @returns {HTMLInputElement|null} The typed-quantity input when the target is one inside a cart view.
 */
function quantityInput(target) {
  if (!(target instanceof HTMLInputElement) || !target.matches(QTY_INPUT_SELECTOR)) return null;
  return target.closest('cart-items-component') ? target : null;
}

/**
 * Schedules a commit of a quantity for a row, 300 ms later; a newer change of the same row restarts the wait.
 * @param {HTMLElement} view
 * @param {string} key
 * @param {string} raw
 */
function scheduleCommit(view, key, raw) {
  const id = `${view.dataset.sectionId}|${key}`;
  const previous = pendingCommits.get(id);
  if (previous) clearTimeout(previous.timer);
  const entry = {
    view,
    key,
    raw,
    timer: window.setTimeout(() => {
      pendingCommits.delete(id);
      commit(entry.view, entry.key, entry.raw);
    }, COMMIT_DELAY_MS),
  };
  pendingCommits.set(id, entry);
}

/**
 * Sends every pending 300 ms commit at once (the size module calls it before it queues a swap).
 */
function flush() {
  const entries = [...pendingCommits.entries()];
  pendingCommits.clear();
  for (const [, entry] of entries) {
    clearTimeout(entry.timer);
    commit(entry.view, entry.key, entry.raw);
  }
}

/**
 * Commits a typed or stepped quantity: validate, compare with the server value, queue a quantity or remove job.
 * @param {HTMLElement} view
 * @param {string} key
 * @param {string} raw
 */
function commit(view, key, raw) {
  const currentView = resolveView({ view, sectionId: view.dataset.sectionId ?? '' });
  const row = currentView ? findRow(currentView, key) : null;
  if (!currentView || !row) return;
  // Horizon's focus handler marked the field to keep its typed value across a morph; the edit is over.
  row.querySelector(QTY_INPUT_SELECTOR)?.removeAttribute('data-skip-value-update');

  const text = raw.trim();
  const quantity = /^\d+$/.test(text) ? Number(text) : NaN;
  if (!Number.isSafeInteger(quantity)) return restoreInput(row);
  routeQuantity(currentView, row, quantity);
}

/**
 * Window capture `click`: Remove routing, swap protection of the drawer, scrim, note toggle guard.
 * @param {MouseEvent} event
 */
function onClick(event) {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;
  const drawer = getDrawer();

  if (active?.kind === 'swap' && drawer && isDrawerDismissal(target, drawer)) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  const remove = target.closest('.cart-items__remove');
  const view = remove?.closest('cart-items-component');
  if (remove && view) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const row = remove.closest('tr[data-key]');
    if (!row || Date.now() - swapEnd < SWAP_GRACE_MS) return;
    const pending = pendingCommits.get(`${/** @type {HTMLElement} */ (view).dataset.sectionId}|${/** @type {HTMLElement} */ (row).dataset.key}`);
    if (pending) {
      clearTimeout(pending.timer);
      pendingCommits.delete(`${/** @type {HTMLElement} */ (view).dataset.sectionId}|${/** @type {HTMLElement} */ (row).dataset.key}`);
    }
    routeQuantity(/** @type {HTMLElement} */ (view), row, 0);
    return;
  }

  if (drawer && target === drawer) {
    event.stopImmediatePropagation();
    /** @type {any} */ (drawer).close?.();
    return;
  }

  if (target.closest('.cart-page .cart-note__summary')) event.preventDefault();
}

/**
 * A click that would close the drawer: its close button, the dialog itself (backdrop below 990 px), the scrim.
 * @param {Element} target
 * @param {HTMLElement} drawer
 * @returns {boolean}
 */
function isDrawerDismissal(target, drawer) {
  if (target === drawer) return true;
  if (!drawer.contains(target)) return false;
  return target.matches('dialog') || target.closest('.theme-drawer__close-button') !== null;
}

/**
 * Window capture `blur` on a typed quantity: Horizon's `setQuantity` (clamps 0 to 1 and writes) must not run.
 * @param {FocusEvent} event
 */
function onBlur(event) {
  const input = quantityInput(event.target);
  if (!input) return;
  event.stopImmediatePropagation();
  input.removeAttribute('data-skip-value-update');
}

/**
 * Window capture `change` on a typed quantity: commit 300 ms later.
 * @param {Event} event
 */
function onChange(event) {
  const input = quantityInput(event.target);
  const view = input?.closest('cart-items-component');
  const key = input?.closest('tr[data-key]')?.getAttribute('data-key');
  if (!input || !view || !key) return;
  scheduleCommit(/** @type {HTMLElement} */ (view), key, input.value);
}

/**
 * Window capture `keydown`: Enter in a typed quantity commits at once and never submits the form;
 * Escape is refused while a size swap runs.
 * @param {KeyboardEvent} event
 */
function onKeydown(event) {
  if (event.key === 'Escape' && !event.isComposing && active?.kind === 'swap') {
    event.preventDefault();
    return;
  }
  if (event.key !== 'Enter' || event.isComposing) return;
  const input = quantityInput(event.target);
  const view = input?.closest('cart-items-component');
  const key = input?.closest('tr[data-key]')?.getAttribute('data-key');
  if (!input || !view || !key) return;
  event.preventDefault();
  const id = `${/** @type {HTMLElement} */ (view).dataset.sectionId}|${key}`;
  const pending = pendingCommits.get(id);
  if (pending) clearTimeout(pending.timer);
  pendingCommits.delete(id);
  commit(/** @type {HTMLElement} */ (view), key, input.value);
}

/**
 * Window capture `quantity-selector:update` from a cart view: Horizon's debounced handler never sees it.
 * @param {Event & { detail?: { quantity?: number } }} event
 */
function onQuantityEvent(event) {
  const target = event.target instanceof Element ? event.target : null;
  const view = target?.closest('cart-items-component');
  const key = target?.closest('tr[data-key]')?.getAttribute('data-key');
  if (!view || !key) return;
  event.stopImmediatePropagation();
  const quantity = Number(event.detail?.quantity);
  if (!Number.isFinite(quantity)) return;
  scheduleCommit(/** @type {HTMLElement} */ (view), key, String(quantity));
}

/**
 * Window capture `cancel` (Escape on the drawer's modal dialog) while a size swap runs.
 * @param {Event} event
 */
function onCancel(event) {
  const drawer = getDrawer();
  if (active?.kind === 'swap' && drawer && event.target instanceof Node && drawer.contains(event.target)) {
    event.preventDefault();
  }
}

/* ------------------------------------------------------------------------------------------------
   Editable quantity for unavailable variants (CART-20 point 5)
   ------------------------------------------------------------------------------------------------ */

/**
 * Removes `disabled` from the quantity field of rows marked `data-zb-qty-editable`.
 * @param {HTMLElement} view
 */
function enableEditableQuantities(view) {
  for (const marker of view.querySelectorAll('[data-zb-qty-editable]')) {
    const input = /** @type {HTMLInputElement|null} */ (marker.closest('tr[data-key]')?.querySelector(QTY_INPUT_SELECTOR));
    if (input?.disabled) input.disabled = false;
  }
}

/**
 * Installs (once per view) an observer that keeps those fields enabled after every morph.
 */
function watchViews() {
  for (const view of views()) {
    if (watchedViews.has(view)) continue;
    watchedViews.add(view);
    enableEditableQuantities(view);
    new MutationObserver(() => enableEditableQuantities(view)).observe(view, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }
}

/**
 * @param {NodeList} nodes
 * @returns {boolean} Whether any node is, or contains, a cart view.
 */
function touchesViews(nodes) {
  for (const node of nodes) {
    if (node instanceof Element && (node.localName === 'cart-items-component' || node.querySelector('cart-items-component'))) {
      return true;
    }
  }
  return false;
}

/**
 * Cart views can be replaced (section morphs, apps): set up new ones, re-apply the busy state and re-check RENDER DONE.
 */
function reconcileViews() {
  watchViews();
  if (!active || isStale(active)) return;
  applyBusy(active.kind);
  active.wake?.();
}

/**
 * Empties the <noscript> blocks that a cart re-render turned into live elements. Fetched section HTML is parsed with
 * scripting off, so <noscript> content becomes real elements: a <style> there applies, a submit button there becomes the
 * default button of #cart-form (QA-061, hz/70). The no-JS fallbacks are only needed without JavaScript, when no re-render
 * happens. The <noscript> element itself stays, so the next morph pairs it with the server's <noscript> and does not
 * replace its siblings. A <noscript> from a normal page load holds only text and is never touched.
 * @param {NodeList} nodes - Added nodes of one mutation record.
 */
function dropLiveNoscript(nodes) {
  const regions = '#cart-drawer, .cart-page';
  for (const node of nodes) {
    if (!(node instanceof Element)) continue;
    if (!node.closest(regions) && !node.querySelector(regions)) continue;
    const found = new Set(node.querySelectorAll('noscript'));
    const own = node.closest('noscript');
    if (own) found.add(own);
    for (const el of found) {
      if (el.firstElementChild && el.closest(regions)) el.replaceChildren();
    }
  }
}

/**
 * Watches the document for cart views being added or removed.
 */
function watchDocument() {
  new MutationObserver((records) => {
    for (const record of records) dropLiveNoscript(record.addedNodes);
    if (records.some((record) => touchesViews(record.addedNodes) || touchesViews(record.removedNodes))) {
      afterFrame(reconcileViews);
    }
  }).observe(document.body, { childList: true, subtree: true });
}

/* ------------------------------------------------------------------------------------------------
   Drawer: opener, desktop overlay, no-persist
   ------------------------------------------------------------------------------------------------ */

/**
 * Opens the cart drawer after RENDER DONE; without a drawer the shopper goes to the cart page.
 * @returns {Promise<boolean>} Whether the drawer was opened.
 */
async function openDrawer() {
  const drawer = /** @type {any} */ (getDrawer());
  if (drawer && typeof drawer.open !== 'function') {
    await Promise.race([customElements.whenDefined('theme-drawer'), new Promise((resolve) => setTimeout(resolve, 3000))]);
  }
  if (!drawer || typeof drawer.open !== 'function') {
    location.href = cartUrl();
    return false;
  }
  if (!drawer.hasAttribute('open')) drawer.open();
  return true;
}

/**
 * Applies the desktop overlay (scroll lock, inert page) to an open cart drawer, or removes it.
 * @param {boolean} on
 */
function setOverlay(on) {
  const drawer = getDrawer();
  const page = document.querySelector('.page-wrapper');
  if (!drawer) return;
  if (on) {
    lockScroll(drawer);
    page?.setAttribute('inert', '');
  } else {
    unlockScroll(drawer);
    page?.removeAttribute('inert');
  }
}

/** Last input modality seen on the window: 'pointer' (mouse, touch, pen) or 'keyboard'. */
let lastModality = 'keyboard';

/**
 * CART-30: when the drawer was opened with a pointer, Horizon's focus on the close button shows no ring
 * (CSS: `[data-zb-pointer-focus]:focus-visible`). The mark is removed on blur and on the next key press.
 * Only the close button is marked, once per open. Keyboard opens keep the visible ring; Horizon's focus order and trap are untouched.
 * @param {HTMLElement} drawer
 */
function markPointerFocus(drawer) {
  if (lastModality !== 'pointer') return;
  const target = document.activeElement;
  if (!(target instanceof HTMLElement) || !drawer.contains(target)) return;
  if (!target.matches('.theme-drawer__close-button') || target.hasAttribute('data-zb-pointer-focus')) return;
  target.setAttribute('data-zb-pointer-focus', '');
  const clear = () => {
    target.removeAttribute('data-zb-pointer-focus');
    target.removeEventListener('blur', clear);
    window.removeEventListener('keydown', clear, true);
  };
  target.addEventListener('blur', clear);
  window.addEventListener('keydown', clear, true);
}

/**
 * Window capture `theme-drawer:open` / `theme-drawer:close` for the cart drawer (runs before the theme's own listeners).
 * @param {Event} event
 * @param {boolean} opening
 * @param {MediaQueryList} wide
 */
function onDrawerEvent(event, opening, wide) {
  const drawer = getDrawer();
  if (!drawer || event.target !== drawer) return;
  if (opening) {
    drawer.setAttribute('no-persist', '');
    setOverlay(wide.matches);
    // The event fires after open() focused the close button; a restored-open drawer never dispatches it.
    markPointerFocus(drawer);
  } else {
    setOverlay(false);
  }
}

/**
 * At load: the cart drawer never persists across pages and is never restored open.
 */
function initDrawer() {
  const drawer = /** @type {any} */ (getDrawer());
  if (!drawer) return;
  drawer.setAttribute('no-persist', '');
  try {
    const list = (sessionStorage.getItem('theme-drawer-open') ?? '').split(',').filter(Boolean);
    const rest = list.filter((id) => id !== 'cart-drawer');
    if (rest.length !== list.length) {
      if (rest.length) sessionStorage.setItem('theme-drawer-open', rest.join(','));
      else sessionStorage.removeItem('theme-drawer-open');
    }
  } catch {
    /* storage unavailable: nothing to clean */
  }
  if (drawer.hasAttribute('open')) {
    const previous = document.activeElement;
    customElements
      .whenDefined('theme-drawer')
      .then(() => drawer.close?.())
      .then(() => {
        // Closing returns focus to the header bag; a restored drawer must not have taken focus anywhere.
        if (previous instanceof HTMLElement && previous !== document.body && previous.isConnected) {
          previous.focus({ preventScroll: true });
        } else if (document.activeElement instanceof HTMLElement && document.activeElement.matches('[aria-controls="cart-drawer"]')) {
          document.activeElement.blur();
        }
      });
  }
}

/* ------------------------------------------------------------------------------------------------
   Checkout busy state (CART-37 / HDR-26)
   ------------------------------------------------------------------------------------------------ */

/** Removes `aria-busy` and `aria-disabled` from every busy checkout button. */
function resetCheckout() {
  clearTimeout(checkoutTimer);
  for (const button of document.querySelectorAll(`${CHECKOUT_SELECTOR}[aria-busy="true"]`)) {
    button.removeAttribute('aria-busy');
    button.removeAttribute('aria-disabled');
  }
  if (active && !isStale(active)) applyBusy(active.kind);
}

/**
 * Document `submit` (bubble phase, so earlier handlers can cancel first).
 * @param {SubmitEvent} event
 */
function onSubmit(event) {
  const submitter = event.submitter;
  if (!(submitter instanceof Element) || !submitter.matches(CHECKOUT_SELECTOR)) return;
  if (event.defaultPrevented) return;
  if (active) {
    event.preventDefault();
    return;
  }
  if (document.querySelector(`${CHECKOUT_SELECTOR}[aria-busy="true"]`)) {
    event.preventDefault();
    return;
  }
  submitter.setAttribute('aria-busy', 'true');
  submitter.setAttribute('aria-disabled', 'true');
  clearTimeout(checkoutTimer);
  checkoutTimer = window.setTimeout(resetCheckout, CHECKOUT_BUSY_MS);
}

/* ------------------------------------------------------------------------------------------------
   TR consent box (step 10; markup printed only on TR by snippets/zerbay-cart-consent.liquid)
   ------------------------------------------------------------------------------------------------ */

const CONSENT_SELECTOR = '[data-zb-consent-input]';

/**
 * @param {EventTarget|null} target
 * @returns {target is HTMLInputElement} Whether the target is the consent checkbox.
 */
function isConsentInput(target) {
  return target instanceof HTMLInputElement && target.matches(CONSENT_SELECTOR);
}

/**
 * Shows or hides the error line under the consent box and mirrors it in `aria-invalid`.
 * @param {HTMLInputElement} input
 * @param {boolean} on
 */
function setConsentError(input, on) {
  const wrapper = input.closest('[data-zb-consent]');
  if (!wrapper) return;
  wrapper.classList.toggle('zb-consent--error', on);
  input.setAttribute('aria-invalid', on ? 'true' : 'false');
  const message = wrapper.querySelector('[data-zb-consent-error]');
  if (message instanceof HTMLElement) message.hidden = !on;
}

/**
 * The checkout form submitted while the box is unticked: the browser's own bubble is replaced by the line under the box.
 * `invalid` does not bubble, hence the capture phase; the browser skips its bubble and its focus when the event is
 * cancelled, so the box is focused here.
 * @param {Event} event
 */
function onConsentInvalid(event) {
  const input = event.target;
  if (!isConsentInput(input)) return;
  event.preventDefault();
  setConsentError(input, true);
  input.focus();
}

/**
 * Tick or untick: error off when ticked, the time into the form's hidden field, the answer saved on the cart.
 * The save changes cart attributes only (no lines, discount or note) and dispatches no Horizon cart event, so it is not
 * a lock job and the lock does not count it as an external write (`onLinesUpdate` / `onOtherCartWrite` never see it)
 * and nothing re-renders because of it: the box state survives. A later real cart change re-renders the cart views
 * from the server, which prints the box unticked again, so the shopper confirms the changed cart again.
 * The time also goes into the hidden field: a tap on checkout right after the tick can outrun (or abort) the save,
 * and the checkout POST then still carries answer, time and version together.
 * @param {Event} event
 */
function onConsentChange(event) {
  const input = event.target;
  if (!isConsentInput(input)) return;
  if (input.checked) setConsentError(input, false);
  const time = input.checked ? new Date().toISOString() : '';
  const timeField = input.closest('[data-zb-consent]')?.querySelector('[data-zb-consent-time]');
  if (timeField instanceof HTMLInputElement) timeField.value = time;
  fetch(`${route('cart_update_url', '/cart/update')}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      attributes: {
        _on_bilgilendirme_onay: input.checked ? 'evet' : '',
        _on_bilgilendirme_zamani: time,
        _on_bilgilendirme_surum: input.dataset.zbConsentVersion || '',
      },
    }),
  }).catch(() => {
    /* the checkout POST carries the same three values from the form, so a failed early save is harmless */
  });
}

/**
 * Delegated listeners (the drawer and /cart re-render their markup after every cart change). Nothing matches on the
 * global store, where no box is printed.
 */
function initConsent() {
  document.addEventListener('invalid', onConsentInvalid, { capture: true });
  document.addEventListener('change', onConsentChange);
}

/* ------------------------------------------------------------------------------------------------
   Start-up
   ------------------------------------------------------------------------------------------------ */

/**
 * Builds the lock and its listeners, publishes `window.Zerbay.cartLock` and announces it.
 */
function bootstrap() {
  const api = {
    /** Queues a cart write; see `run`. */
    run,
    /** @returns {boolean} Whether a writer holds the lock. */
    get busy() {
      return active !== null;
    },
    /** @returns {string} The running job's kind, or ''. */
    get kind() {
      return active?.kind ?? '';
    },
    showError,
    strings,
    /** @returns {number} Timestamp (ms) of the last finished size swap. */
    get lastSwapEnd() {
      return swapEnd;
    },
    /** Called by the size module when a swap job has finished. */
    markSwapEnd() {
      swapEnd = Date.now();
    },
    flush,
  };

  const wide = window.matchMedia(WIDE_QUERY);

  window.addEventListener('pointerdown', () => { lastModality = 'pointer'; }, { capture: true });
  window.addEventListener('keydown', () => { lastModality = 'keyboard'; }, { capture: true });
  window.addEventListener('click', onClick, { capture: true });
  window.addEventListener('blur', onBlur, { capture: true });
  window.addEventListener('change', onChange, { capture: true });
  window.addEventListener('keydown', onKeydown, { capture: true });
  window.addEventListener('cancel', onCancel, { capture: true });
  window.addEventListener(ThemeEvents.quantitySelectorUpdate, onQuantityEvent, { capture: true });
  window.addEventListener('theme-drawer:open', (event) => onDrawerEvent(event, true, wide), { capture: true });
  window.addEventListener('theme-drawer:close', (event) => onDrawerEvent(event, false, wide), { capture: true });
  wide.addEventListener('change', () => {
    const drawer = getDrawer();
    if (drawer?.hasAttribute('open')) setOverlay(wide.matches);
  });
  document.addEventListener(StandardEvents.cartLinesUpdate, onLinesUpdate, { capture: true });
  document.addEventListener(StandardEvents.cartDiscountUpdate, onOtherCartWrite, { capture: true });
  document.addEventListener(StandardEvents.cartNoteUpdate, onOtherCartWrite, { capture: true });
  document.addEventListener(StandardEvents.cartError, /** @type {EventListener} */ (onCartError));
  document.addEventListener('submit', /** @type {EventListener} */ (onSubmit));
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetCheckout();
  });

  initDrawer();
  initConsent();
  watchViews();
  watchDocument();

  window.Zerbay = window.Zerbay || {};
  window.Zerbay.cartLock = Object.freeze(api);
  document.dispatchEvent(new CustomEvent('zerbay:cart-lock-ready'));
}

if (!window.Zerbay?.cartLock) bootstrap();
